import { useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "../api";
import { Markdown } from "../markdown";
import type { TraceEntry } from "../types";

/** The trace is read every few seconds while the worker runs; afterwards once, for the record. */
const TRACE_POLL_MS = 2000;

/**
 * What a loop iteration's or a one-shot run's worker has done so far, from its trace: what it said,
 * which tools it called on what, and what came back. Open while it runs, so the page shows progress
 * rather than a status pill; folded once it has ended. A native iteration has no trace and draws
 * nothing here. Once the worker has ended while the run goes on, the platform is running its checks,
 * which a line under the trace says, `endedNote` in place of the plain one; `onEnded` hears it.
 */
export function RunTrace({
	loopId,
	iterationId,
	running,
	endedNote,
	onEnded,
}: {
	loopId: string;
	iterationId: string;
	running: boolean;
	endedNote?: ReactNode;
	onEnded?: (ended: boolean) => void;
}) {
	const [entries, setEntries] = useState<TraceEntry[]>([]);
	const [done, setDone] = useState(false);
	// Where the next read starts; the end of a run reads on from here rather than over again.
	const from = useRef(0);

	useEffect(() => {
		from.current = 0;
		setEntries([]);
		setDone(false);
	}, [loopId, iterationId]);

	useEffect(() => {
		onEnded?.(done);
	}, [done, onEnded]);

	useEffect(() => {
		let cancelled = false;
		const read = async () => {
			try {
				const chunk = await api.loopTrace(loopId, iterationId, from.current);
				if (cancelled) return;
				from.current = chunk.next;
				if (chunk.entries.length > 0) {
					setEntries((prev) => [...prev, ...chunk.entries]);
					// Ended when the last entry is a result: a run started over after one that ended goes on.
					setDone(chunk.entries.at(-1)!.kind === "result");
				}
			} catch {
				// A missing trace or a hiccup: the next poll reads again.
			}
		};
		void read();
		if (!running) return () => void (cancelled = true);
		const timer = setInterval(() => void read(), TRACE_POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [loopId, iterationId, running]);

	if (entries.length === 0) return running ? <p className="hint">等待 worker 启动…</p> : null;
	const tools = entries.filter((e) => e.kind === "tool").length;
	return (
		<>
			<details className="acceptance trace" open={running}>
				<summary>
					过程 <span className="hint">{tools} 次工具调用{running && !done ? " · 进行中" : ""}</span>
				</summary>
				<div className="trace-entries">
					{entries.map((e, i) => (
						<TraceLine key={i} entry={e} />
					))}
				</div>
			</details>
			{running && done && <p className="hint trace-after">{endedNote ?? "验收中…"}</p>}
		</>
	);
}

function TraceLine({ entry }: { entry: TraceEntry }) {
	switch (entry.kind) {
		case "text":
			return (
				<div className="trace-text">
					<Markdown text={entry.text} />
				</div>
			);
		case "tool":
			return (
				<div className="trace-tool">
					<code>{entry.name}</code> <span>{entry.text}</span>
				</div>
			);
		case "tool_result":
			return (
				<details className={`trace-result${entry.error ? " error" : ""}`}>
					<summary>{entry.error ? "出错" : "结果"}</summary>
					<pre className="loop-output">{entry.text || "（无输出）"}</pre>
				</details>
			);
		default:
			return <div className={`trace-mark${entry.error ? " error" : ""}`}>{entry.text}</div>;
	}
}
